
import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, Download, Filter, Calendar } from 'lucide-react';

const VolumeChart = ({ 
  data: initialData, 
  title, 
  timePeriod, 
  setTimePeriod, 
  comparisonType, 
  setComparisonType,
  fuelType,
  setFuelType 
}) => {
  // Process data based on timePeriod, comparisonType, and fuelType
  const data = useMemo(() => {
    // Filter or transform data based on timePeriod
    let filteredData = [...initialData];
    
    // Apply time period filtering
    switch(timePeriod) {
      case 'daily':
        // For demo purposes, we'll just take the most recent 7 data points
        filteredData = initialData.slice(-7);
        break;
      case 'weekly':
        // For demo purposes, we'll take every other data point
        filteredData = initialData.filter((_, index) => index % 2 === 0);
        break;
      case 'quarterly':
        // For demo, take first month of each quarter
        filteredData = initialData.filter((_, index) => index % 3 === 0);
        break;
      case 'ytd':
        // For demo, take data from current year (assuming data is for current year)
        filteredData = initialData.slice(0);
        break;
      case 'monthly':
      default:
        // Use all data (monthly view is default)
        filteredData = initialData;
        break;
    }
    
    // Apply fuel type filtering
    if (fuelType !== 'all') {
      filteredData = filteredData.map(item => {
        // Different multipliers depending on product type
        let multiplier = 1.0;
        switch(fuelType) {
          case 'petrol':
            multiplier = 0.6; // 60% petrol
            break;
          case 'diesel':
            multiplier = 0.4; // 40% diesel
            break;
          case 'premium':
            multiplier = 0.15; // 15% premium fuels
            break;
          case 'e20':
            multiplier = 0.12; // 12% E20
            break;
          case 'e85':
            multiplier = 0.08; // 8% E85
            break;
          case 'ev':
            multiplier = 0.05; // 5% EV charging
            break;
          case 'lpg':
            multiplier = 0.2; // 20% LPG
            break;
          case 'ngv':
            multiplier = 0.1; // 10% NGV
            break;
          default:
            multiplier = 1.0;
        }
        return {
          ...item,
          retailVolume: Math.round(item.retailVolume * multiplier),
          target: Math.round(item.target * multiplier)
        };
      });
    }
    
    // Transform data based on comparisonType
    switch(comparisonType) {
      case 'previous':
        // Shift target values to show previous period
        return filteredData.map((item, index, array) => ({
          ...item,
          previousPeriod: index > 0 ? array[index-1].retailVolume : null,
          comparisonLabel: 'Previous Period'
        }));
      case 'lastyear':
        // Simulate last year's data (for demo - 10% lower)
        return filteredData.map(item => ({
          ...item,
          previousPeriod: item.retailVolume * 0.9,
          comparisonLabel: 'Last Year'
        }));
      case 'target':
      default:
        // Use target as comparison (default)
        return filteredData.map(item => ({
          ...item,
          comparisonLabel: 'Target'
        }));
    }
  }, [initialData, timePeriod, comparisonType, fuelType]);

  // Get the most recent month's data for comparison
  const latestMonth = data[data.length - 1];
  const previousMonth = data[data.length - 2];
  
  // Calculate percent change
  const percentChange = latestMonth && previousMonth 
    ? (((latestMonth.retailVolume - previousMonth.retailVolume) / previousMonth.retailVolume) * 100).toFixed(1)
    : 0;
  
  // Determine if trending up or down
  const isTrendingUp = percentChange >= 0;

  // Get product label for display
  const getProductLabel = () => {
    switch(fuelType) {
      case 'petrol': return 'Petrol Only';
      case 'diesel': return 'Diesel Only';
      case 'premium': return 'Premium Fuels';
      case 'e20': return 'E20 Gasohol';
      case 'e85': return 'E85 Gasohol';
      case 'ev': return 'EV Charging';
      case 'lpg': return 'LPG';
      case 'ngv': return 'NGV (Natural Gas)';
      case 'all':
      default: return 'All Products';
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow p-6 col-span-2">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <TrendingUp size={20} className="mr-2 text-purple-600" />
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2">
            <Filter size={14} className="text-gray-500" />
            <select 
              className="bg-white border border-gray-300 rounded-md px-2 py-1 text-xs"
              value={timePeriod}
              onChange={(e) => setTimePeriod(e.target.value)}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="ytd">Year-to-Date</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">Compare:</span>
            <select 
              className="bg-white border border-gray-300 rounded-md px-2 py-1 text-xs"
              value={comparisonType}
              onChange={(e) => setComparisonType(e.target.value)}
            >
              <option value="previous">Previous Period</option>
              <option value="lastyear">Same Period Last Year</option>
              <option value="target">Target</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">Product:</span>
            <select 
              className="bg-white border border-gray-300 rounded-md px-2 py-1 text-xs"
              value={fuelType}
              onChange={(e) => setFuelType(e.target.value)}
            >
              <option value="all">All Products</option>
              <option value="petrol">Petrol</option>
              <option value="diesel">Diesel</option>
              <option value="premium">Premium Fuels</option>
              <option value="e20">E20 Gasohol</option>
              <option value="e85">E85 Gasohol</option>
              <option value="lpg">LPG</option>
              <option value="ngv">NGV</option>
              <option value="ev">EV Charging</option>
            </select>
          </div>
          
          <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center ${
            isTrendingUp ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            <span>{isTrendingUp ? '+' : ''}{percentChange}%</span>
          </div>
          <button className="p-1 rounded-full hover:bg-gray-100">
            <Download size={16} className="text-gray-600" />
          </button>
        </div>
      </div>
      
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
            <XAxis dataKey="month" tick={{ fill: '#424242' }} />
            <YAxis tick={{ fill: '#424242' }} domain={[0, dataMax => Math.max(dataMax, 3200)]} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff',
                border: '1px solid #E0E0E0',
                borderRadius: '4px'
              }} 
              formatter={(value, name) => {
                if (name === 'retailVolume') return [value, 'Actual Volume'];
                if (name === 'previousPeriod') return [value, data[0]?.comparisonLabel || 'Comparison'];
                return [value, name === 'target' ? 'Target Volume' : name];
              }}
            />
            <Legend />
            <ReferenceLine y={3100} stroke="#E53E3E" strokeDasharray="3 3" label={{
              position: 'right',
              value: 'Target',
              fill: '#E53E3E',
              fontSize: 12
            }} />
            <Line 
              type="monotone" 
              dataKey="retailVolume" 
              stroke="#A100FF" 
              strokeWidth={2} 
              dot={{ r: 4, fill: '#A100FF' }}
              name="Actual Volume"
              activeDot={{ r: 6, fill: '#A100FF' }}
            />
            
            {comparisonType === 'target' ? (
              <Line 
                type="monotone" 
                dataKey="target" 
                stroke="#D6ABFF" 
                strokeWidth={2} 
                strokeDasharray="5 5"
                dot={false}
                name="Target Volume"
              />
            ) : (
              <Line 
                type="monotone" 
                dataKey="previousPeriod" 
                stroke="#D6ABFF" 
                strokeWidth={2} 
                strokeDasharray="5 5"
                dot={false}
                name={data[0]?.comparisonLabel || 'Comparison'}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4 flex justify-between items-center text-xs text-gray-500">
        <div>
          {getProductLabel()}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-purple-600 mr-1"></div>
            <span>Actual</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-purple-300 mr-1"></div>
            <span>{comparisonType === 'target' ? 'Target' : data[0]?.comparisonLabel || 'Comparison'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VolumeChart;
